var _class, _descriptor, _descriptor2;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { later } from '@ember/runloop';
import { all } from 'rsvp';
import { isNone, isPresent, isEmpty } from '@ember/utils';
import { CatalogErrorType, errorIsCatalogError } from '@square/dashboard-data/utils/catalog/catalog-error';
// eslint-disable-next-line no-restricted-imports
import { imageLibraryImagesUploaded } from '@dashboard/shared-constants/catalog/eventstream/items/event-catalog';
import { v4 as uuidv4 } from 'uuid';
import Image from '@square/dashboard-items-data/models/connect/image';
import { tracked } from '@glimmer/tracking';
const RateLimitRetryTimeout = 2000;
export const FILE_TYPE_WHITE_LIST = new Set(['image/jpeg', 'image/pjpeg', 'image/png', 'image/gif']);
/**
 * This class exists to support the `image-upload` service. The service will instantiate objects of this class
 * for each image file passed in to upload. Objects of this class expose a data url as the `url` attribute so they can be used by most
 * of the components we use for displaying images in the item library in place of a Connect Image model.
 */
export let UploadQueueEntry = (_class = class UploadQueueEntry {
  constructor({
    imageFile,
    store
  }) {
    _defineProperty(this, "imageFile", void 0);
    _defineProperty(this, "store", void 0);
    _defineProperty(this, "promise", void 0);
    _defineProperty(this, "upload_id", void 0);
    _initializerDefineProperty(this, "url", _descriptor, this);
    _initializerDefineProperty(this, "isUploading", _descriptor2, this);
    _defineProperty(this, "resolve", void 0);
    this.imageFile = imageFile;
    this.store = store;
    this._readFileAsDataUrl(imageFile);
    this.resolve = () => ({}); // making a silly assignment here to make Typescript linter happy
    this.promise = new Promise(resolve => {
      this.resolve = resolve;
    });

    // upload_id is used to uniquely identify and filter out the correct UploadQueueEntry from the uploadQueue inside the image-upload
    // service.
    this.upload_id = uuidv4();
  }
  _readFileAsDataUrl(imageFile) {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      if (!fileReader.result) {
        return;
      }
      this.url = fileReader.result;
    };
    fileReader.readAsDataURL(imageFile);
  }

  // Starts the process of uploading the file to the server. Once upload is finished the exposed promise will either
  // resolve to the Connect Image model or reject. Automatically retries on rate limit errors.
  async startUpload() {
    this.isUploading = true;
    try {
      const imageAdapter = await this.store.adapterFor('connect/image');
      const imageObject = await imageAdapter.uploadImage(this.imageFile, {
        name: this.imageFile.name
      });
      this.resolve({
        success: true,
        imageObject
      });
    } catch (error) {
      if (errorIsCatalogError(error) && error.catalogErrorType === CatalogErrorType.RATE_LIMIT) {
        later(this, 'startUpload', RateLimitRetryTimeout);
      } else {
        const xhrError = error;
        this.resolve({
          success: false,
          error: xhrError
        });
      }
    }
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "url", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "isUploading", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
})), _class);

/**
 * This function can be used in contexts where we are displaying a list of images, where some of the images might be UploadQueueEntry
 * objects for images that are being uploaded asynchronously. If you pass in an array of mixed models and entry objects this function
 * will set up promise handlers to automatically update the array when the uploads finish.
 *
 * If a resolved image from an upload already exists in the array it will not add duplicates.
 *
 * If the UploadQueueEntry object is no longer in the array when the promise resolves the resolved image will not be added to the array.
 *
 * When using this function make sure other application code modifies the passed images array in place rather than replacing it when making edits,
 * as that could result in the automatic update logic updating an array that is no longer being used in the UI.
 *
 * @param {Array<Object>} imagesArray
 */
export function updateArrayAsUploadsFinish(imagesArray) {
  imagesArray.filter(isUploadQueueEntry).forEach(uploadQueueEntry => {
    uploadQueueEntry.promise.then(({
      success,
      imageObject
    }) => {
      if (imageObject && success) {
        const uploadEntryIndex = imagesArray.indexOf(uploadQueueEntry);
        if (uploadEntryIndex > -1) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const objectsToInsert = imagesArray.includes(imageObject) ? [] : [imageObject];

          // @ts-expect-error TODO: [can] replace exists on arrays due to ember-array prototype extension.
          // Converting these to native arrays breaks functionality because ember array supports updating the UI when it is
          // modified in place, whereas native javascript array does not
          imagesArray.replace(uploadEntryIndex, 1, objectsToInsert);
        }
      } else {
        // @ts-expect-error TODO: [can] removeObject exists on arrays due to ember-array prototype extension
        // Converting these to native arrays breaks functionality because ember array supports updating the UI when it is
        // modified in place, whereas native javascript array does not
        imagesArray.removeObject(uploadQueueEntry);
      }
    });
  });
}
export function validateFileType(file) {
  if (isNone(file)) {
    return false;
  }
  let valid = false;
  if (isPresent(file.type)) {
    valid = FILE_TYPE_WHITE_LIST.has(file.type);
  }
  return valid;
}

// [can] declaring eventstream here because @square/dashboard-items-data packages can't depend on @dashboard definitions

/**
 * This function waits for all the passed uploads to finish and then resolves an object with two attributes:
 *   success: Whether or not all the uploads were successful. If any uploads failed, this will be false.
 *   errorMessage: If success == false, the translated error message to display to the user, else undefined.
 */
export async function waitForUploads(images, eventstreamService, imageEventProperties) {
  const uploadQueueEntries = images.filter(isUploadQueueEntry);
  const errorPromises = uploadQueueEntries.map(async uploadQueueEntry => {
    const {
      success,
      error
    } = await uploadQueueEntry.promise;
    if (success) {
      return null;
    }
    if (error && errorIsCatalogError(error)) {
      return error.catalogErrorType;
    }
    return 'OTHER';
  });
  const errorsOrNull = await all(errorPromises);
  const errors = errorsOrNull.filter(Boolean);
  const successes = errorsOrNull.filter(promises => promises === null).length;
  successes && eventstreamService.track(imageLibraryImagesUploaded, {
    ...imageEventProperties,
    details: successes
  });
  return {
    success: isEmpty(errors),
    errors
  };
}
export function isImageUpload(object) {
  return object instanceof Image;
}
export function isUploadQueueEntry(object) {
  return object instanceof UploadQueueEntry;
}